import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import {
  TooltipComponent,
  GridComponent,
  LegendComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { useAppContext } from 'providers/AppProvider';
import dayjs from 'dayjs';
import { OrderData } from 'api/dashboard/_request';

echarts.use([TooltipComponent, GridComponent, LegendComponent, LineChart]);

type EcomReturningCustomerRateChartProps = {
  data: OrderData[];
  range: 'today' | 'week' | 'month' | 'year';
};

const generateBuckets = (range: string): string[] => {
  const now = dayjs();
  const buckets: string[] = [];

  if (range === 'today') {
    const start = now.startOf('day');
    for (let i = 0; i < 24; i += 3) {
      buckets.push(start.add(i, 'hour').format('HH') + ' hrs');
    }
  } else if (range === 'week') {
    const start = now.subtract(6, 'day').startOf('day');
    for (let i = 0; i < 7; i++) {
      buckets.push(start.add(i, 'day').format('DD-MMM'));
    }
  } else if (range === 'month') {
    const start = now.subtract(30, 'day').startOf('day');
    for (let i = 0; i <= 30; i += 3) {
      buckets.push(start.add(i, 'day').format('DD-MMM'));
    }
  } else if (range === 'year') {
    for (let i = 0; i < 12; i++) {
      buckets.push(dayjs().month(i).format('MMM'));
    }
  }

  return buckets;
};

const mapOrderToBucket = (order: OrderData, range: string): string => {
  const createdAt = dayjs(order.createdAt);
  if (range === 'today') {
    const bucketHour = Math.floor(createdAt.hour() / 3) * 3;
    return (
      createdAt.startOf('day').add(bucketHour, 'hour').format('HH') + ' hrs'
    );
  } else if (range === 'week' || range === 'month') {
    return createdAt.format('DD-MMM');
  } else if (range === 'year') {
    return createdAt.format('MMM');
  }
  return '';
};

const processData = (orders: OrderData[], range: string) => {
  const buckets = generateBuckets(range);
  const userOrdersMap: Record<string, OrderData[]> = {};

  // Group orders by userId
  for (const order of orders) {
    if (!userOrdersMap[order.userId]) {
      userOrdersMap[order.userId] = [];
    }
    userOrdersMap[order.userId].push(order);
  }

  const bucketCounts: Record<string, Record<string, number>> = {
    '2nd Time': Object.fromEntries(buckets.map(b => [b, 0])),
    '3rd Time': Object.fromEntries(buckets.map(b => [b, 0])),
    '4th Time': Object.fromEntries(buckets.map(b => [b, 0])),
    'More than 4 Times': Object.fromEntries(buckets.map(b => [b, 0]))
  };

  for (const userOrders of Object.values(userOrdersMap)) {
    const sortedOrders = userOrders.sort(
      (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix()
    );
    const totalOrders = sortedOrders.length;
    if (totalOrders < 2) continue; // Skip if not repeat customer

    const lastOrder = sortedOrders[totalOrders - 1];
    const bucket = mapOrderToBucket(lastOrder, range);
    if (!buckets.includes(bucket)) continue;

    if (totalOrders === 2) bucketCounts['2nd Time'][bucket]++;
    else if (totalOrders === 3) bucketCounts['3rd Time'][bucket]++;
    else if (totalOrders === 4) bucketCounts['4th Time'][bucket]++;
    else if (totalOrders > 4) bucketCounts['More than 4 Times'][bucket]++;
  }

  const series = Object.entries(bucketCounts).map(([name, values]) => ({
    name,
    type: 'line',
    data: buckets.map(bucket => values[bucket]),
    showSymbol: false,
    symbol: 'circle',
    lineStyle: {
      width: name === '2nd Time' ? 3 : 1,
      type: name === 'More than 4 Times' ? 'dashed' : 'solid'
    }
  }));

  return { labels: buckets, series };
};

const EcomReturningCustomerRateChart = ({
  data,
  range
}: EcomReturningCustomerRateChartProps) => {
  const { getThemeColor } = useAppContext();
  const { labels, series } = useMemo(
    () => processData(data, range),
    [data, range]
  );

  const option = {
    color: [
      getThemeColor('primary-light'),
      getThemeColor('info-lighter'),
      getThemeColor('warning'),
      getThemeColor('error')
    ],
    tooltip: {
      trigger: 'axis',
      formatter: (
        params: {
          axisValue: string;
          marker: string;
          seriesName: string;
          data: number;
        }[]
      ) => {
        const lines = [`<strong>${params[0].axisValue}</strong>`];
        params.forEach(
          (p: {
            axisValue: string;
            marker: string;
            seriesName: string;
            data: number;
          }) => {
            lines.push(
              `${p.marker} ${p.seriesName}: <strong>${p.data}</strong>`
            );
          }
        );
        return lines.join('<br/>');
      }
    },
    legend: {
      data: ['2nd Time', '3rd Time', '4th Time', 'More than 4 Times'],
      textStyle: {
        color: getThemeColor('body-color'),
        fontWeight: 600,
        fontFamily: 'Nunito Sans'
      },
      right: 'right'
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        color: getThemeColor('secondary-color'),
        fontWeight: 600,
        fontSize: 12
      },
      axisLine: {
        lineStyle: { color: getThemeColor('tertiary-bg') }
      },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('secondary-bg'), type: 'dashed' }
      }
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: {
        color: getThemeColor('secondary-color'),
        fontWeight: 600
      },
      splitLine: {
        show: true,
        lineStyle: { color: getThemeColor('secondary-bg') }
      }
    },
    series,
    grid: { left: 0, right: 8, top: '14%', bottom: 0, containLabel: true }
  };

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      style={{ height: '300px', width: '100%' }}
    />
  );
};

export default EcomReturningCustomerRateChart;
